import { isAxiosError } from "axios";
import type {
  CompanyLookupResult,
  MyPayEnvironment,
} from "../../generated/core-api";
import {
  fetchOnboardingCompanyLookup,
  OnboardingCompanyNotFoundError,
} from "../../shared/hooks/onboardingCompanyLookup";

const UNAUTHORIZED_STATUS = 401;
const FORBIDDEN_STATUS = 403;

/** Generic retry copy for any failure that isn't "not found" or "forbidden". */
const RETRY_ERROR_MESSAGE =
  "Could not confirm in Datapay; retry or check Company List.";

export type OnboardingCompanyLookupStatus = "resolved" | "fallback" | "error";

export interface OnboardingCompanyLookupRowResult {
  status: OnboardingCompanyLookupStatus;
  lookup: CompanyLookupResult | null;
  errorMessage: string | null;
}

/**
 * Resolve one company via `GET /api/v1/onboarding/companies/{code}` for bulk-create
 * Results read-back. Uses the shared lookup in `onboardingCompanyLookup.ts`.
 *
 * Only 401 rejects (for `UnauthorizedInterceptor`); every other failure
 * resolves to a per-row `"error"` so one lookup can't abort the whole batch.
 */
export async function lookupOnboardingCompany(
  companyCode: string,
  mypayEnvironment: MyPayEnvironment
): Promise<OnboardingCompanyLookupRowResult> {
  const trimmed = companyCode.trim();
  if (!trimmed) {
    return { status: "fallback", lookup: null, errorMessage: null };
  }

  try {
    const lookup = await fetchOnboardingCompanyLookup(trimmed, mypayEnvironment);
    return {
      status: "resolved",
      lookup,
      errorMessage: null,
    };
  } catch (error) {
    if (error instanceof OnboardingCompanyNotFoundError) {
      return { status: "fallback", lookup: null, errorMessage: null };
    }
    if (isAxiosError(error) && error.response?.status === UNAUTHORIZED_STATUS) {
      // Propagate for UnauthorizedInterceptor / caller.
      throw error;
    }
    if (isAxiosError(error) && error.response?.status === FORBIDDEN_STATUS) {
      return {
        status: "error",
        lookup: null,
        errorMessage:
          "You don't have permission to view this company in Datapay.",
      };
    }
    // Any other failure (5xx, network error, unexpected shape, etc.) stays
    // a per-row error.
    return {
      status: "error",
      lookup: null,
      errorMessage: RETRY_ERROR_MESSAGE,
    };
  }
}
